/** Admin shipment boxes (`/v1/shipments/admin/.../boxes`) — active boxes only. */

export interface ShipmentBox {
  id: number;
  /** Display sequence from the API. May be omitted on older rows. */
  box_seq?: number | null;
  length: number;
  breadth: number;
  height: number;
  weight: number;
  awb?: string | null;
  piece_seq?: number | string | null;
  label_s3_key?: string | null;
  created_date?: Date | string | null;
  modified_date?: Date | string | null;
}

export interface AddShipmentBoxRequest {
  length: number;
  breadth: number;
  height: number;
  weight: number;
  awb?: string | null;
}

export interface AddShipmentBoxResult {
  shipmentId: number;
  box: ShipmentBox;
  boxes: ShipmentBox[];
}

export interface DeleteShipmentBoxResult {
  shipmentId: number;
  boxId: number;
  boxes: ShipmentBox[];
}

export type ShipmentBoxErrorCode = "SHIPMENT_NOT_FOUND" | "BOXES_NOT_EDITABLE";

export type BulkAddShipmentBoxFailure = {
  shipmentId: number;
  message: string;
  addedCount: number;
  requestedCount: number;
  failedBoxIndex?: number;
};

export type BulkAddShipmentBoxResult = {
  succeeded: number[];
  failed: BulkAddShipmentBoxFailure[];
  boxesAdded: number;
};

export type ShipmentBoxDraftDimensions = {
  length: unknown;
  breadth: unknown;
  height: unknown;
  weight: unknown;
  awb?: unknown;
};

export function getShipmentBoxSeq(
  box: Pick<ShipmentBox, "box_seq">,
): number | null {
  try {
    if (box.box_seq == null) {
      return null;
    }
    const parsed = Number(box.box_seq);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function getShipmentBoxAwb(
  box?: Pick<ShipmentBox, "awb"> | null,
): string | null {
  try {
    const awb = String(box?.awb ?? "").trim();
    return awb ? awb : null;
  } catch {
    return null;
  }
}

/** Locked when an AWB is assigned (non-empty trimmed string). */
export function isShipmentBoxLocked(
  box?: Pick<ShipmentBox, "awb"> | null,
): boolean {
  try {
    return getShipmentBoxAwb(box) != null;
  } catch {
    return false;
  }
}

export function formatShipmentBoxPieceSeq(
  box?: Pick<ShipmentBox, "piece_seq"> | null,
): string | null {
  try {
    if (box?.piece_seq == null) {
      return null;
    }
    const formatted = String(box.piece_seq).trim();
    return formatted ? formatted : null;
  } catch {
    return null;
  }
}

export function getShipmentBoxLabelS3Key(
  box?: Pick<ShipmentBox, "label_s3_key"> | null,
): string | null {
  try {
    const key = String(box?.label_s3_key ?? "").trim();
    return key ? key : null;
  } catch {
    return null;
  }
}

export function formatShipmentBoxListLabel(
  box: Pick<ShipmentBox, "id" | "box_seq">,
): string {
  try {
    const seq = getShipmentBoxSeq(box);
    if (seq != null) {
      return `Box ${seq} · #${box.id}`;
    }
    return `Box #${box.id}`;
  } catch {
    return `Box #${box.id}`;
  }
}

export function sortShipmentBoxesByIdAsc(
  boxes: ShipmentBox[] | null | undefined,
): ShipmentBox[] {
  return [...(boxes ?? [])].sort((a, b) => a.id - b.id);
}

/** Listing order: `box_seq` ascending, then `id`. Booking still uses lowest id. */
export function sortShipmentBoxesForDisplay(
  boxes: ShipmentBox[] | null | undefined,
): ShipmentBox[] {
  try {
    return [...(boxes ?? [])].sort((a, b) => {
      const aSeq = getShipmentBoxSeq(a);
      const bSeq = getShipmentBoxSeq(b);
      const aOrder = aSeq ?? Number.POSITIVE_INFINITY;
      const bOrder = bSeq ?? Number.POSITIVE_INFINITY;
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return a.id - b.id;
    });
  } catch {
    return sortShipmentBoxesByIdAsc(boxes);
  }
}

export function getFirstActiveShipmentBox(
  boxes: ShipmentBox[] | null | undefined,
): ShipmentBox | null {
  return sortShipmentBoxesByIdAsc(boxes)[0] ?? null;
}

export function isPositiveBoxDimension(value: unknown): boolean {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
}

export function parseShipmentBoxAwb(value: unknown): string | undefined {
  try {
    const awb = String(value ?? "").trim();
    return awb ? awb : undefined;
  } catch {
    return undefined;
  }
}

export function parseShipmentBoxDimensions(
  input: ShipmentBoxDraftDimensions,
): AddShipmentBoxRequest | null {
  const length = Number(input.length);
  const breadth = Number(input.breadth);
  const height = Number(input.height);
  const weight = Number(input.weight);

  if (
    ![length, breadth, height, weight].every((value) =>
      isPositiveBoxDimension(value),
    )
  ) {
    return null;
  }

  const payload: AddShipmentBoxRequest = { length, breadth, height, weight };
  const awb = parseShipmentBoxAwb(input.awb);
  if (awb) {
    payload.awb = awb;
  }

  return payload;
}

export function hasAnyShipmentBoxDraftValue(
  input: ShipmentBoxDraftDimensions,
): boolean {
  return [input.length, input.breadth, input.height, input.weight].some(
    (value) => String(value ?? "").trim() !== "",
  );
}

export function getFilledShipmentBoxPayloads(
  drafts: ShipmentBoxDraftDimensions[],
): {
  payloads: AddShipmentBoxRequest[];
  partialRowNumbers: number[];
} {
  const payloads: AddShipmentBoxRequest[] = [];
  const partialRowNumbers: number[] = [];

  drafts.forEach((draft, index) => {
    try {
      if (!hasAnyShipmentBoxDraftValue(draft)) {
        return;
      }

      const payload = parseShipmentBoxDimensions(draft);
      if (payload) {
        payloads.push(payload);
        return;
      }

      partialRowNumbers.push(index + 1);
    } catch {
      partialRowNumbers.push(index + 1);
    }
  });

  return { payloads, partialRowNumbers };
}

export function getBulkAddShipmentBoxesSummary(
  result: BulkAddShipmentBoxResult,
  shipmentCount: number,
): string {
  const boxWord = result.boxesAdded === 1 ? "box" : "boxes";
  const shipmentWord = shipmentCount === 1 ? "shipment" : "shipments";
  const parts = [
    `Added ${result.boxesAdded} ${boxWord} to ${result.succeeded.length} of ${shipmentCount} ${shipmentWord}.`,
  ];

  if (result.failed.length > 0) {
    const failedDetails = result.failed
      .slice(0, 8)
      .map((item) => {
        const progress =
          item.requestedCount > 1
            ? `added ${item.addedCount} of ${item.requestedCount} boxes; `
            : "";
        return `#${item.shipmentId} (${progress}${item.message})`;
      })
      .join("; ");
    const extraCount = result.failed.length - 8;
    parts.push(
      `Failed: ${failedDetails}${
        extraCount > 0 ? `; and ${extraCount} more` : ""
      }.`,
    );
  }

  return parts.join(" ");
}

export function formatShipmentBoxDimensions(
  box: Pick<ShipmentBox, "length" | "breadth" | "height" | "weight">,
): string {
  return `${box.length} × ${box.breadth} × ${box.height} cm · ${box.weight} kg`;
}

export function getShipmentBoxApiErrorMessage(
  error: unknown,
  fallback = "Failed to update shipment boxes.",
): string {
  const data = (
    error as {
      response?: {
        data?: {
          message?: string;
          code?: string;
        };
      };
      message?: string;
    }
  )?.response?.data;

  return data?.message || (error as { message?: string })?.message || fallback;
}
